if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // MERELDON: the image of the variant currently selected (set by Liquid, then by colour changes).
        this.currentVariantMediaId = this.dataset.variantMediaId || null;

        this.scheduleInitialReveal();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => this.setActiveMedia(mediaToSwitch.dataset.target, false, false, 'thumbnail'));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      /*
       * MERELDON: the gallery always keeps the product's own image order.
       * Choosing a colour (source 'variant', called by product-info.js) slides the gallery to that
       * colour's image instead of moving the image to the front. The second argument (Dawn's old
       * "prepend" flag) is accepted for compatibility but never reorders anything.
       */
      setActiveMedia(mediaId, _prepend = false, preserveViewport = false, source = 'variant') {
        const isVariantChange = source !== 'thumbnail';

        // A size change keeps the same colour image: leave the gallery exactly where the shopper left it.
        if (isVariantChange && mediaId === this.currentVariantMediaId) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        if (isVariantChange) this.currentVariantMediaId = mediaId;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.loadEagerly(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            this.scrollViewerTo(activeMedia);
          }
          // Keep the gallery's own slide updated, but never move the entire
          // document for a variant chosen from the bottom sticky bar.
          if (preserveViewport === true) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (isVariantChange) {
            // Colour change: only bring the gallery back into view when most of it is off screen.
            const visibleHeight =
              Math.min(activeMediaRect.bottom, window.innerHeight) - Math.max(activeMediaRect.top, 0);
            if (activeMediaRect.height === 0 || visibleHeight >= activeMediaRect.height / 2) return;
          } else if (activeMediaRect.top > -0.5) {
            // Thumbnail click: don't scroll if the image is already in view
            return;
          }
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // Scroll the main gallery (not the page) so the item is the first one in view.
      scrollViewerTo(item, instant = false) {
        const list = item.parentElement;
        if (!list) return;
        const left = list.scrollLeft + item.getBoundingClientRect().left - list.getBoundingClientRect().left;
        if (instant) {
          const previousBehavior = list.style.scrollBehavior;
          list.style.scrollBehavior = 'auto';
          list.scrollLeft = left;
          list.style.scrollBehavior = previousBehavior;
        } else {
          list.scrollTo({ left });
        }
      }

      // Images further along the gallery are lazy; load the target (and its neighbour) straight away.
      loadEagerly(item) {
        [item, item?.nextElementSibling].forEach((element) => {
          element?.querySelectorAll('img[loading="lazy"]').forEach((image) => {
            image.loading = 'eager';
          });
        });
      }

      // When the page opens on a specific colour (?variant=…), start the gallery on that colour's image.
      scheduleInitialReveal() {
        const reveal = () => {
          const active = this.elements.viewer?.querySelector('[data-media-id].is-active');
          if (!active || !active.parentElement) return;
          if (active.parentElement.clientWidth === 0) return;
          if (active !== active.parentElement.firstElementChild && (!this.mql.matches || this.elements.thumbnails)) {
            this.scrollViewerTo(active, true);
          }
          const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${active.dataset.mediaId}"]`);
          if (thumbnail) this.setActiveThumbnail(thumbnail, true);
        };
        requestAnimationFrame(() => requestAnimationFrame(reveal));
      }

      setActiveThumbnail(thumbnail, instant = false) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.scrollThumbnailIntoView(thumbnail, instant);
      }

      // Works for the horizontal rail (mobile) and the vertical rail (desktop).
      scrollThumbnailIntoView(thumbnail, instant = false) {
        const list = this.elements.thumbnails.slider || thumbnail.parentElement;
        if (!list) return;
        const listRect = list.getBoundingClientRect();
        const itemRect = thumbnail.getBoundingClientRect();
        const vertical = list.scrollHeight > list.clientHeight + 1 && list.scrollWidth <= list.clientWidth + 1;
        const tolerance = 10;
        let target;

        if (vertical) {
          if (itemRect.top >= listRect.top - tolerance && itemRect.bottom <= listRect.bottom + tolerance) return;
          target = { top: list.scrollTop + itemRect.top - listRect.top };
        } else {
          if (itemRect.left >= listRect.left - tolerance && itemRect.right <= listRect.right + tolerance) return;
          target = { left: list.scrollLeft + itemRect.left - listRect.left };
        }

        if (instant) {
          const previousBehavior = list.style.scrollBehavior;
          list.style.scrollBehavior = 'auto';
          if (target.top !== undefined) list.scrollTop = target.top;
          if (target.left !== undefined) list.scrollLeft = target.left;
          list.style.scrollBehavior = previousBehavior;
        } else {
          list.scrollTo(target);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
